import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideRefreshCw,
  lucideHeartPulse,
  lucideCloud,
} from '@ng-icons/lucide';
import { HlmIcon } from '@spartan-ng/helm/icon';

import { HlmTabs } from '@spartan-ng/helm/tabs';
import { HlmTabsList } from '@spartan-ng/helm/tabs';
import { HlmTabsTrigger } from '@spartan-ng/helm/tabs';
import { HlmTabsContent } from '@spartan-ng/helm/tabs';
import { HlmSwitch } from '@spartan-ng/helm/switch';
import { HlmLabel } from '@spartan-ng/helm/label';
import { HlmButton } from '@spartan-ng/helm/button';

import { HealthTabComponent } from './tabs/health-tab/health-tab.component';
import { IntegrationsTabComponent } from './tabs/integrations-tab/integrations-tab.component';

const VALID_TABS = ['health', 'integrations'] as const;
type TabId = (typeof VALID_TABS)[number];

@Component({
  selector: 'app-system-health',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './system-health.component.html',
  styleUrls: ['./system-health.component.css'],
  providers: [
    provideIcons({ lucideRefreshCw, lucideHeartPulse, lucideCloud }),
  ],
  imports: [
    DatePipe,
    NgIcon,
    HlmIcon,
    HlmTabs,
    HlmTabsList,
    HlmTabsTrigger,
    HlmTabsContent,
    HlmSwitch,
    HlmLabel,
    HlmButton,
    HealthTabComponent,
    IntegrationsTabComponent,
  ],
})
export class SystemHealthComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  readonly activeTab = signal<TabId>('health');
  readonly autoRefreshEnabled = signal(false);
  readonly lastRefreshed = signal(new Date());

  ngOnInit(): void {
    this.route.queryParamMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(params => {
        const tab = params.get('tab') as TabId | null;
        if (tab && (VALID_TABS as readonly string[]).includes(tab)) {
          this.activeTab.set(tab);
        }
      });
  }

  onTabChange(tab: string): void {
    if ((VALID_TABS as readonly string[]).includes(tab)) {
      this.activeTab.set(tab as TabId);
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { tab },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    }
  }

  toggleAutoRefresh(checked: boolean): void {
    this.autoRefreshEnabled.set(checked);
    if (checked) {
      this.lastRefreshed.set(new Date());
    }
  }

  manualRefresh(): void {
    this.lastRefreshed.set(new Date());
  }
}
